/**
 * Atlas PM · 系统设置 · 任务形态字典面板（P4.6 / P7.3）。
 *
 * 平铺列表（本任务不做层级树；P7.3 已删除未使用的「上级」列）。
 *
 * 数据源：GET /api/system/task-categories（含停用项）；权限：dict:manage。
 */

import { useState } from 'react';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import Panel from '../../../components/Panel';
import Spinner from '../../../components/Spinner';
import DeleteTaskCategoryDialog from './DeleteTaskCategoryDialog';
import TaskCategoryDialog from './TaskCategoryDialog';
import { type TaskCategoryItem } from './api';
import { useTaskCategoryList } from './useTaskCategories';
import './task-categories.css';

interface DialogState {
  mode: 'create' | 'edit';
  category?: TaskCategoryItem | null;
}

export default function TaskCategoriesPanel() {
  const listQ = useTaskCategoryList();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TaskCategoryItem | null>(null);

  const items = listQ.data ?? [];

  const openCreate = () => setDialog({ mode: 'create' });
  const openEdit = (category: TaskCategoryItem) => setDialog({ mode: 'edit', category });

  return (
    <Panel
      title="任务形态"
      extra={
        <Button variant="primary" size="sm" data-testid="tc-new" onClick={openCreate}>
          ＋ 新建形态
        </Button>
      }
      flush
    >
      {listQ.isLoading ? (
        <Spinner label="加载任务形态…" />
      ) : listQ.isError ? (
        <div className="tc-error">
          ⚠ {listQ.error instanceof Error ? listQ.error.message : '任务形态加载失败'}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="◇"
          title="暂无任务形态"
          hint="点击右上角「新建形态」添加第一条任务形态字典。"
        />
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table" data-testid="tc-table">
            <thead>
              <tr>
                <th>代码</th>
                <th>名称</th>
                <th>备注说明</th>
                <th>颜色</th>
                <th>排序</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid="tc-row" data-code={it.code}>
                  <td className="pm-cell--code">{it.code}</td>
                  <td className="pm-cell--name" data-testid="tc-name">
                    {it.name}
                  </td>
                  <td
                    className="tc-desc-cell"
                    data-testid="tc-description"
                    title={it.description ?? undefined}
                  >
                    {it.description || '—'}
                  </td>
                  <td>
                    <span className="tc-color-cell">
                      {it.color ? (
                        <span className="tc-swatch" style={{ background: it.color }} aria-hidden />
                      ) : (
                        <span className="tc-swatch is-empty" aria-hidden />
                      )}
                      <span className="tc-color-code num">{it.color ?? '—'}</span>
                    </span>
                  </td>
                  <td className="num">{it.sortOrder}</td>
                  <td>
                    {it.isActive ? (
                      <Badge tone="ok">启用</Badge>
                    ) : (
                      <Badge tone="idle">停用</Badge>
                    )}
                  </td>
                  <td>
                    <div className="pm-actions">
                      <button
                        type="button"
                        className="pm-linkbtn"
                        data-testid="tc-edit"
                        onClick={() => openEdit(it)}
                      >
                        编辑
                      </button>
                      <button
                        type="button"
                        className="pm-linkbtn is-danger"
                        data-testid="tc-delete"
                        onClick={() => setPendingDelete(it)}
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TaskCategoryDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        category={dialog?.category ?? null}
        onClose={() => setDialog(null)}
      />

      <DeleteTaskCategoryDialog
        open={pendingDelete !== null}
        category={pendingDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Panel>
  );
}
